import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ComponentSync, EditableDBItemV3, getElementCenterPos, Grid, LL_V_L, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, mouseInteractivity_ToolTip, scrollToTop, Show, stopPropagation, TS_CollapsableContainer, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import './Editor_FHIRResourceSubType.scss';
import { ICONS } from '@app/styles';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText, DefaultEditor_InputText_Optional, DefaultEditor_TextArea } from '@app/styles/frontend/ui-manager/ui-components';
import { _keys, asArray, sortArray } from '@nu-art/ts-common';
import { ModuleFE_FHIRResourceParam, ModuleFE_FHIRResourceSubtype } from '../../../_entity.js';
import { Editor_FHIRResourceSubTypeParam } from '../Editor_FHIRResourceSubTypeParam/Editor_FHIRResourceSubTypeParam.js';
const viewMode_View = 'view';
const viewMode_Edit = 'edit';
export class Editor_FHIRResourceSubType extends ComponentSync {
    subTypeRef = React.createRef();
    scrollToParamId;
    // ######################## Lifecycle ########################
    __onFHIRResourceParamsUpdated = (...params) => {
        if (!['delete', 'delete-multi'].includes(params[0]))
            return;
        if (asArray(params[1]).some(param => param.resourceSubtypeId === this.state.editable.item._id))
            this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        state.editable = this.getEditable(nextProps.subType);
        state.editMode = nextProps.editMode;
        state.subTypeScrollRef = nextProps.subTypeScrollRef;
        return state;
    }
    componentDidMount() {
        //Only once when this component is first rendered, check if scroll needs to be triggered
        if (this.props.triggerScroll)
            scrollToTop(this.subTypeRef, this.state.subTypeScrollRef);
    }
    // ######################## Logic ########################
    getEditable(subType) {
        return new EditableDBItemV3(subType, ModuleFE_FHIRResourceSubtype)
            .setAutoSave(!!subType._id)
            .setDebounceTimeout(300)
            .setOnSaveCompleted(() => this.forceUpdate());
    }
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    createNewParam = async (e) => {
        const model = {
            id: 'editor__fhir-resource-sub-type__add-param-popup',
            modalPos: { x: 1, y: 0 },
            originPos: getElementCenterPos(e.currentTarget),
            offset: { x: 15 },
            content: () => _jsxs(_Fragment, { children: [_jsx("div", { ...this.openToolTip('fixed'), children: "Fixed" }), _jsx("div", { ...this.openToolTip('dynamic'), children: "Dynamic" })] }),
        };
        ModuleFE_MouseInteractivity.showContent(model);
    };
    openToolTip = (paramType) => {
        return {
            onMouseEnter: (e) => {
                const box = e.currentTarget.getBoundingClientRect();
                const model = {
                    id: 'editor__fhir-resource-sub-type__add-param-tooltip',
                    modalPos: { x: 1, y: 0 },
                    originPos: { x: box.right, y: box.bottom },
                    contentHoverDelay: 1,
                    content: () => _jsxs(_Fragment, { children: [_jsx("div", { onClick: () => this.createNewParamImpl(paramType, false), children: "Single Value" }), _jsx("div", { onClick: () => this.createNewParamImpl(paramType, true), children: "Multi Value" })] })
                };
                ModuleFE_MouseInteractivity.showTooltip(model);
            },
            onMouseLeave: () => ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip),
        };
    };
    createNewParamImpl = async (type, isMultiValue) => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip);
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        const subType = this.state.editable.item;
        try {
            const newParam = {
                type,
                isMultiValue,
                key: 'new-param',
                isOptional: true,
                isPostFiltered: false,
                resourceSubtypeId: subType._id,
            };
            const param = await ModuleFE_FHIRResourceParam.v1.upsert(newParam).executeSync();
            this.scrollToParamId = param._id;
            this.forceUpdate();
        }
        catch (err) {
            this.logError(err);
        }
    };
    // ######################## Render ########################
    render() {
        const subType = this.state.editable;
        return _jsx(TS_CollapsableContainer, { className: 'editor__fhir-resource-sub-type', flipHeaderOrder: true, customCaret: _jsx(ICONS.advisor_v5_arrow.component, {}), headerRenderer: this.renderHeader, containerRenderer: this.renderBody, innerRef: this.subTypeRef }, subType.item._id);
    }
    renderHeader = () => {
        const subType = this.state.editable;
        return _jsxs(Show, { children: [_jsx(Show.If, { condition: true, children: subType.item.label }), _jsx(Show.If, { condition: this.state.editMode, children: _jsx(ICONS.bin.component, { onClick: e => {
                            stopPropagation(e);
                            subType.delete();
                        } }) })] });
    };
    renderBody = () => {
        return _jsxs(_Fragment, { children: [this.renderEditor_Label(), this.renderEditor_Description(), this.renderEditor_GroupParams(), this.renderEditor_Params()] });
    };
    // ######################## Render - Editors ########################
    renderEditor_Label = () => {
        const subType = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: subType.item.label }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: subType, prop: 'label' }) }
                ] }) });
    };
    renderEditor_Description = () => {
        const subType = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Description', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: subType.item.description }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_TextArea, { resizeWithText: true, editable: subType, prop: 'description' }) }
                ] }) });
    };
    renderEditor_GroupParams = () => {
        const groupParams = this.state.editable.editProp('groupParams', {});
        const groupParamKeys = _keys(groupParams.item);
        if (!groupParamKeys.length)
            return;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Group Params', children: _jsxs(Grid, { className: 'editor__fhir-resource-sub-type__group-params', children: [_jsx("div", { className: 'editor__fhir-resource-sub-type__value bold', children: "Param key" }), _jsx("div", { className: 'editor__fhir-resource-sub-type__value bold', children: "Param Label" }), _jsx("div", { className: 'editor__fhir-resource-sub-type__value bold', children: "Param Value" }), groupParamKeys.map(key => {
                        const editable = groupParams.editProp(key, []);
                        return this.renderEditor_GroupParams_Param_Multi(key, editable);
                    })] }) });
    };
    renderEditor_GroupParams_Param_Multi = (key, editable) => {
        return _jsx(React.Fragment, { children: editable.item.map((_param, index) => {
                const param = editable.editProp(index, {});
                return _jsxs(React.Fragment, { children: [_jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: key }), _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                                { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: param.item.label ?? 'N/A' }) },
                                { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText_Optional, { editable: param, prop: 'label' }) }
                            ] }), _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                                { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: param.item.value ?? 'N/A' }) },
                                { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText_Optional, { editable: param, prop: 'value' }) }
                            ] })] }, `${key}-${index}`);
            }) }, key);
    };
    renderEditor_Params = () => {
        const params = ModuleFE_FHIRResourceParam.cache.filter(param => param.resourceSubtypeId === this.state.editable.item._id);
        sortArray(params, param => param.key);
        return _jsxs(TS_PropRenderer.Vertical, { label: this.renderEditor_Params_Label(), children: [!params.length && _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: "No params defined" }), _jsx(LL_V_L, { className: 'editor__fhir-resource-sub-type__params', children: params.map((param, index) => {
                        const triggerScroll = this.scrollToParamId === param._id;
                        return _jsx(Editor_FHIRResourceSubTypeParam, { param: param, editMode: this.state.editMode, subTypeScrollRef: this.state.subTypeScrollRef, triggerScroll: triggerScroll }, param._id);
                    }) })] });
    };
    renderEditor_Params_Label = () => {
        return _jsxs(_Fragment, { children: ["Params", this.state.editMode && _jsx(ICONS.advisor_avn_add.component, { onClick: this.createNewParam })] });
    };
}
//# sourceMappingURL=Editor_FHIRResourceSubType.js.map